import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../store';

// TODO: abi는 컨트랙트에 맞게 변경되어야 함 (현재는 임시 abis)
// load abis json file
// example erc20
import chains, { Chain, ChainSymbol } from '../../datas/chains';
import tokens, { Token } from '../../datas/tokens';
import {
  ChainTransferBridgeFee,
  HistoryPayload,
} from '../../services/bridge-backend.service';

export const STORAGE_SELECTED_TOKEN_KEY = `selectedTokenSymbol`;
export const STORAGE_SELECTED_FROM_CHAIN_KEY = `selectedFromChainSymbol`;

declare global {
  interface Window {
    ethereum?: any;
    besu?: any;
    mdl?: any;
    klaytn?: any;
    _ethereum?: any;
  }
}

export interface ChainHtlc {
  isNative: boolean;
  address: string;
  chainSymbol: ChainSymbol;
}

interface ServerChain {
  id: number;
  symbol: ChainSymbol;
  evmChainId: number;
  chainTokenList: ChainToken[];
  chainHtlcList: ChainHtlc[];
}

export interface ChainExtended extends Chain, ServerChain {}

export interface TokenExtended extends Token {
  supportedChains: Chain[];
}

export interface ChainToken extends Token {
  chainSymbol: ChainSymbol;
  // 컨트랙트 주소
  address: string;
}

interface CurrentHistory extends HistoryPayload {
  preimg: string;
}

type BridgeState = {
  // 토큰 리스트
  tokens: TokenExtended[];
  selectedToken: TokenExtended | null;
  chains: ChainExtended[];
  selectedFromChain: ChainExtended | null | undefined;
  selectedToChain: ChainExtended | null;

  // ChainToken, ChainHtlc는 reducer에 의해 자동으로 결정됨
  fromChainToken: ChainToken | null;
  toChainToken: ChainToken | null;
  fromChainHtlc: ChainHtlc | null;
  toChainHtlc: ChainHtlc | null;

  tokenTransferAmount: null | string;
  // 4단계 관련 변수
  txIsOngoing: boolean;
  txCurrentHistory: null | CurrentHistory;
  txHasError: boolean;

  // timeout될시 logout
  timeoutLogout: boolean;

  chainTransferBridgeFee: null | ChainTransferBridgeFee;

  isConfirm: boolean;
  isApproval: boolean;

  convertMaxAmount: string | null;
  convertMinAmount: string | null;

  amountRange: string | null;

  convertNow: boolean;
};

const initialState: BridgeState = {
  tokens: tokens.map((token) => ({
    ...token,
    supportedChains: [],
  })),
  selectedToken: null,
  chains: [],
  selectedFromChain: null,
  selectedToChain: null,
  fromChainToken: null,
  toChainToken: null,
  fromChainHtlc: null,
  toChainHtlc: null,
  tokenTransferAmount: null,
  txIsOngoing: false,
  txCurrentHistory: null,
  txHasError: false,
  timeoutLogout: false,
  chainTransferBridgeFee: null,
  isConfirm: false,
  isApproval: false,
  convertMaxAmount: null,
  convertMinAmount: null,
  amountRange: null,
  convertNow: false,
};

export const getChains = createAsyncThunk<ServerChain[]>(
  `bridge/getChains`,
  async () => {
    const res = await fetch(
      `${process.env.REACT_APP_KSTADIUM_BRIDGE_BACKEND_URL}/api/chains`,
      {
        method: `GET`,
        headers: {
          'Content-Type': `application/json`,
        },
      },
    );

    return res.json();
  },
);

const bridgeSlice = createSlice({
  name: `bridge`,
  initialState,
  reducers: {
    resetBridge: (state) => {
      sessionStorage.removeItem(STORAGE_SELECTED_TOKEN_KEY);
      sessionStorage.removeItem(STORAGE_SELECTED_FROM_CHAIN_KEY);

      return {
        ...initialState,
        chains: state.chains,
        tokens: state.tokens,
      };
    },
    resetBridgeWithoutToken: (state) => {
      sessionStorage.removeItem(STORAGE_SELECTED_TOKEN_KEY);

      return {
        ...initialState,
        selectedToken: state.selectedToken,
        chains: state.chains,
        tokens: state.tokens,
      };
    },
    setSelectedToken: (
      state,
      action: PayloadAction<BridgeState['selectedToken']>,
    ) => {
      if (action.payload) {
        sessionStorage.setItem(
          STORAGE_SELECTED_TOKEN_KEY,
          action.payload.symbol,
        );
      }

      state.selectedToken = action.payload;
    },
    setSelectedFromChain: (
      state,
      action: PayloadAction<BridgeState['selectedFromChain']>,
    ) => {
      if (action.payload) {
        sessionStorage.setItem(
          STORAGE_SELECTED_FROM_CHAIN_KEY,
          action.payload.symbol,
        );

        const foundChainToken = action.payload.chainTokenList.find(
          (token) =>
            state.selectedToken &&
            token.symbol.includes(state.selectedToken?.symbol),
        );
        state.fromChainToken = foundChainToken || null;

        const isNativeToken =
          foundChainToken && foundChainToken.address === null;
        const foundChainHtlc = action.payload.chainHtlcList.find(
          (htlc) => htlc.isNative === isNativeToken,
        );

        state.fromChainHtlc = foundChainHtlc || null;
      } else {
        state.fromChainToken = null;
        state.fromChainHtlc = null;
      }

      state.selectedFromChain = action.payload;
    },
    setSelectedToChain: (
      state,
      action: PayloadAction<BridgeState['selectedToChain']>,
    ) => {
      if (action.payload) {
        const foundChainToken = action.payload.chainTokenList.find(
          (token) =>
            state.selectedToken &&
            token.symbol.includes(state.selectedToken?.symbol),
        );

        state.toChainToken = foundChainToken || null;

        const isNativeToken =
          foundChainToken && foundChainToken.address === null;
        const foundChainHtlc = action.payload.chainHtlcList.find(
          (htlc) => htlc.isNative === isNativeToken,
        );

        state.toChainHtlc = foundChainHtlc || null;
      }

      state.selectedToChain = action.payload;
    },

    setTxCurrentHistory: (
      state,
      action: PayloadAction<BridgeState['txCurrentHistory']>,
    ) => {
      state.txCurrentHistory = action.payload;
    },

    setTxIsOngoing: (state, action) => {
      state.txIsOngoing = action.payload;
    },

    setTxHasError: (state, action) => {
      state.txHasError = action.payload;
    },

    setTimeoutLogout: (state, action) => {
      state.timeoutLogout = action.payload;
    },

    setTokenTransferAmount: (state, action) => {
      state.tokenTransferAmount = action.payload;
    },

    setChainTransferBridgeFee: (state, action) => {
      state.chainTransferBridgeFee = action.payload;
    },
    setIsConfirm: (state, action) => {
      state.isConfirm = action.payload;
    },
    setIsApproval: (state, action) => {
      state.isApproval = action.payload;
    },
    setConvertMaxAmount: (state, action) => {
      state.convertMaxAmount = action.payload;
    },
    setConvertMinAmount: (state, action) => {
      state.convertMinAmount = action.payload;
    },
    setAmountRage: (state, action) => {
      state.amountRange = action.payload;
    },
    setConvertNow: (state, action) => {
      state.convertNow = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getChains.fulfilled, (state, action) => {
      const serverChains = action.payload;
      // 로컬 데이터에 서버에서 받아온 데이터 덮어씌워서 정보 확장
      state.chains = chains.map((chain) => {
        const serverChain = serverChains.find((c) => c.symbol === chain.symbol);
        return {
          ...chain,
          ...serverChain,
        } as ChainExtended;
      });
    });
  },
});

export const {
  resetBridge,
  resetBridgeWithoutToken,
  setSelectedToken,
  setSelectedFromChain,
  setSelectedToChain,
  setTokenTransferAmount,
  setTxCurrentHistory,
  setTxIsOngoing,
  setTxHasError,
  setTimeoutLogout,
  setChainTransferBridgeFee,
  setIsConfirm,
  setIsApproval,
  setConvertMaxAmount,
  setConvertMinAmount,
  setAmountRage,
  setConvertNow,
} = bridgeSlice.actions;

export default bridgeSlice.reducer;

export const selectBridgeSlice = (state: RootState): BridgeState =>
  state.bridge;
